/**
 * A person's own mail settings: the address their letters start from, and
 * their signatures. Opened from the account menu and from the composer's
 * signature menu; the composer keeps its letter open underneath.
 *
 * Only the caller's own (`crm-email/user-mail-prefs.routes.ts`). The mailbox's
 * shared settings stay under Settings -> Integrations -> Email, and are what a
 * person who has set nothing here still gets.
 *
 * A person with no signature of their own is offered one already filled in
 * (their name, the usual closing, the company card), so one press of Save is
 * enough. Nothing is stored until Save: closing the window keeps the shared one.
 */
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { LoadingState } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  getMyMailSettings,
  saveMyMailSettings,
  type MyMailSettings,
  type OwnSignature,
} from "../../../../lib/mailSignature";
import { Btn, Modal, SelectInput, useToast } from "../../../ui";
import { useCrmUser } from "../../../shell/CrmUser";
import { FieldBox, Note } from "../../settings/panel/fields";
import { senderLine } from "../SenderPick";
import { OwnSignatureRow } from "./OwnSignatureRow";
import { settleDefault, starterSignature } from "./lib";

/** The value the sender picker gives the mailbox's own default. Not an address. */
const MAILBOX = "";

export function MyMailDialog({ onClose, onSaved }: { onClose: () => void; onSaved?: () => void }) {
  const t = useT();
  const toast = useToast();
  const user = useCrmUser();
  const [view, setView] = useState<MyMailSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [rows, setRows] = useState<OwnSignature[]>([]);
  const [defaultId, setDefaultId] = useState("");
  const [from, setFrom] = useState(MAILBOX);
  /** The rows are the filled-in first one, not anything saved. */
  const [starter, setStarter] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getMyMailSettings()
      .then((v) => {
        if (!alive) return;
        setView(v);
        setFrom(v.fromAddress);
        if (v.signatures.length > 0) {
          setRows(v.signatures);
          setDefaultId(v.defaultId);
          return;
        }
        const first = starterSignature({
          label: t("myMail.starterLabel"),
          closing: t("myMail.starterClosing"),
          name: user.name || "",
          card: Boolean(v.company),
        });
        setRows([first]);
        setDefaultId(first.id);
        setStarter(true);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
    // Once per opening: the account does not change while the window is up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeDefault = settleDefault(rows, defaultId);

  function patch(id: string, part: Partial<OwnSignature>) {
    setRows((list) => list.map((r) => (r.id === id ? { ...r, ...part } : r)));
  }

  function remove(id: string) {
    setRows((list) => list.filter((r) => r.id !== id));
  }

  function add() {
    const row = starterSignature({
      label: "",
      closing: t("myMail.starterClosing"),
      name: user.name || "",
      card: Boolean(view?.company),
    });
    setRows((list) => [...list, row]);
  }

  async function save() {
    setBusy(true);
    try {
      const saved = await saveMyMailSettings({ signatures: rows, defaultId: activeDefault, fromAddress: from });
      setView(saved);
      toast(t("myMail.saved"));
      onSaved?.();
      onClose();
    } catch (err) {
      // `ApiError` carries the server's error code as its message.
      toast(err instanceof ApiError && err.message === "unknown_sender" ? t("myMail.unknownSender") : t("myMail.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  const tooMany = view ? rows.length >= view.limits.list : true;

  return (
    <Modal
      title={t("myMail.title")}
      onClose={onClose}
      className="w-[min(640px,100%)]"
      footer={
        <>
          <Btn type="button" onClick={onClose}>
            {t("common.cancel")}
          </Btn>
          <Btn type="button" tone="dark" disabled={!view || busy} onClick={() => void save()}>
            {t("common.save")}
          </Btn>
        </>
      }
    >
      <div className="space-y-6 px-5 pb-3 pt-1">
        {failed ? (
          <Note tone="bad">{t("myMail.loadFailed")}</Note>
        ) : !view ? (
          <LoadingState variant="inline" label={t("common.loading")} />
        ) : (
          <>
            <section className="space-y-2">
              <h3 className="text-[13.25px] font-[650]">{t("myMail.senderTitle")}</h3>
              {view.senders.length > 1 ? (
                <FieldBox label={t("myMail.sender")} hint={t("myMail.senderHint")}>
                  <SelectInput value={from} onChange={setFrom} label={t("myMail.sender")}>
                    <option value={MAILBOX}>{t("myMail.senderMailbox", { address: view.mailboxDefault })}</option>
                    {view.senders.map((s) => (
                      <option key={s.address} value={s.address}>
                        {senderLine(s, s.address)}
                      </option>
                    ))}
                  </SelectInput>
                </FieldBox>
              ) : (
                <p className="crm-sub !mt-0 text-[12.5px] leading-snug">
                  {t("myMail.senderOnly", { address: view.mailboxDefault || "-" })}
                </p>
              )}
            </section>

            <section className="space-y-3">
              <div>
                <h3 className="text-[13.25px] font-[650]">{t("myMail.signaturesTitle")}</h3>
                <p className="crm-sub !mt-0.5 text-[12px] leading-snug">{t("myMail.signaturesHint")}</p>
              </div>

              {starter ? <Note tone="info">{t("myMail.starterNote")}</Note> : null}
              {rows.length === 0 ? <Note tone="mut">{t("myMail.emptyNote")}</Note> : null}

              {rows.map((row) => (
                <OwnSignatureRow
                  key={row.id}
                  sig={row}
                  isDefault={row.id === activeDefault}
                  company={view.company}
                  limits={view.limits}
                  onChange={(part) => patch(row.id, part)}
                  onMakeDefault={() => setDefaultId(row.id)}
                  onRemove={() => remove(row.id)}
                />
              ))}

              <Btn small type="button" onClick={add} disabled={tooMany}>
                <Plus className="size-3.5" />
                {t("myMail.add")}
              </Btn>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
